import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Building2, Globe2, PauseCircle, ShieldOff, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import consoleApi from "@/lib/consoleApi";
import { StatusPill } from "@/components/console/StatusPill";

function Kpi({ label, value, icon: Icon, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="console-card p-5"
    >
      <div className="mb-3 flex items-center justify-between">
        <span className="text-[12px] uppercase tracking-wide text-slate-500">{label}</span>
        <Icon className="h-4 w-4 text-teal-300/80" />
      </div>
      <div className="console-display text-3xl font-bold tabular-nums text-white">{value ?? "â€”"}</div>
    </motion.div>
  );
}

export default function ConsoleDashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ["console-dashboard"],
    queryFn: async () => {
      const { data: res } = await consoleApi.get("/console/dashboard");
      return res;
    },
  });

  const kpis = data?.kpis || {};
  const recent = data?.recent || [];

  return (
    <div>
      <div className="mb-8">
        <h1 className="console-display text-3xl font-bold tracking-tight text-white">Dashboard</h1>
        <p className="mt-1 text-[14px] text-slate-400">
          License health across all Restaurant ERP client companies
        </p>
      </div>

      <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Kpi label="Companies" value={kpis.total_companies} icon={Building2} delay={0.05} />
        <Kpi label="Active" value={kpis.active} icon={Sparkles} delay={0.1} />
        <Kpi label="Suspended" value={kpis.suspended} icon={PauseCircle} delay={0.15} />
        <Kpi label="Blocked" value={kpis.blocked} icon={ShieldOff} delay={0.2} />
        <Kpi label="Online enabled" value={kpis.online_enabled} icon={Globe2} delay={0.25} />
      </div>

      <div className="console-card overflow-hidden">
        <div className="flex items-center justify-between border-b border-white/5 px-5 py-4">
          <h2 className="text-[15px] font-semibold text-white">Recent registrations</h2>
          <Link to="/console/companies" className="text-[12px] font-medium text-teal-300 hover:text-teal-200">
            View all
          </Link>
        </div>
        {isLoading ? (
          <div className="px-5 py-10 text-center text-[13px] text-slate-500">Loadingâ€¦</div>
        ) : recent.length === 0 ? (
          <div className="px-5 py-10 text-center text-[13px] text-slate-500">No companies yet</div>
        ) : (
          <table className="w-full text-left text-[13px]">
            <thead className="text-[11px] uppercase tracking-wide text-slate-500">
              <tr className="border-b border-white/5">
                <th className="px-5 py-3 font-medium">Company</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Registered</th>
                <th className="px-5 py-3 font-medium">Online</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((c, i) => (
                <motion.tr
                  key={c.id}
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  className="border-b border-white/[0.04] hover:bg-white/[0.02]"
                >
                  <td className="px-5 py-3">
                    <Link to={`/console/companies/${c.id}`} className="font-medium text-slate-100 hover:text-teal-200">
                      {c.name}
                    </Link>
                    <div className="text-[11px] text-slate-500">{c.slug}</div>
                  </td>
                  <td className="px-5 py-3">
                    <StatusPill status={c.status} />
                  </td>
                  <td className="px-5 py-3 text-slate-400">
                    {c.registered_at ? new Date(c.registered_at).toLocaleDateString() : "â€”"}
                  </td>
                  <td className="px-5 py-3">
                    {c.features?.online ? (
                      <span className="text-teal-300">On</span>
                    ) : (
                      <span className="text-slate-500">Off</span>
                    )}
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
